import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase/firebaseConfig";
import { collection, getDocs, doc, updateDoc, setDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { FaUser, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { ClipLoader } from "react-spinners";

const AdminList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);

      const adminList = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.admin || userData.wasAdmin) {
          adminList.push({ id: doc.id, ...userData });
        }
      });

      setAdmins(adminList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to fetch admins!");
      setLoading(false);
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        admin: !currentStatus,
        wasAdmin: true,
      });
      toast.success(`Admin status updated successfully!`);
      fetchAdmins();
    } catch (error) {
      console.error("Error updating admin status:", error);
      toast.error("Failed to update admin status!");
    }
  };

  const createAdmin = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, {
        email: email,
        admin: true,
        wasAdmin: true,
      });

      toast.success("Admin created successfully!");
      setEmail("");
      setPassword("");
      fetchAdmins(); // Refresh the list
    } catch (error) {
      console.error("Error creating admin:", error);
      toast.error("Failed to create admin!");
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchAdmins();
    }
  }, [isModalOpen]);

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full flex items-center space-x-2 p-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
      >
        <FaUser className="text-lg" />
        <span>Admin List</span>
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Admin List</h2>

            {/* Create New Admin Button */}
            <button
              onClick={() => {
                // Scroll to the form when the button is clicked
                document.getElementById("create-admin-form").scrollIntoView({ behavior: "smooth" });
              }}
              className="w-full flex items-center space-x-2 p-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors mb-4"
            >
              <FaPlus className="text-lg" />
              <span>Create New Admin</span>
            </button>

            {loading ? (
              <div className="flex justify-center items-center h-40">
                <ClipLoader color="#4B5563" size={40} />
              </div>
            ) : (
              <div className="space-y-4">
                {admins.length === 0 ? (
                  <p className="text-center text-gray-500">No admins found.</p>
                ) : (
                  admins.map((admin) => (
                    <div
                      key={admin.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg shadow-sm"
                    >
                      <div className="flex items-center space-x-3">
                        <FaUser className="text-gray-600" />
                        <span className="text-gray-700">{admin.email}</span>
                        {admin.wasAdmin && !admin.admin && (
                          <span className="text-xs text-gray-500">(Previously Admin)</span>
                        )}
                      </div>

                      <button
                        onClick={() => toggleAdminStatus(admin.id, admin.admin)}
                        className={`flex items-center space-x-2 p-2 rounded-md ${
                          admin.admin
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-gray-800 hover:bg-gray-700"
                        } text-white transition-colors`}
                      >
                        {admin.admin ? (
                          <FaToggleOff className="text-lg" />
                        ) : (
                          <FaToggleOn className="text-lg" />
                        )}
                        <span>{admin.admin ? "Disable Admin" : "Enable Admin"}</span>
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Create Admin Form (Always Visible) */}
            <div id="create-admin-form" className="mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Admin</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                </div>
                <button
                  onClick={createAdmin}
                  className="w-full flex items-center space-x-2 p-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  <FaPlus className="text-lg" />
                  <span>Create Admin</span>
                </button>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom scrollbar styles */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }

          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
      </style>
    </>
  );
};

export default AdminList;
